// Single source of truth for each command's --help text.
//
// WHY this file exists: index.ts must be able to print `<cmd> --help` WITHOUT
// dispatching into (and therefore importing + evaluating) the heavy command
// module behind it. So the help strings live here — a side-effect-free module of
// pure template literals with ZERO heavy dependencies (no api-client, no context
// graph). index.ts imports these eagerly (help is on the fast path); each command
// module re-exports its own const from here so there's still ONE definition.
//
// The help-consistency spec pins this: the text must NOT be re-inlined into
// index.ts, and each command file must keep exporting its const (via re-export).

export const NOTE_HELP = `driftless note add --content "..." [--json]

Capture a note — a gotcha, a decision, an observation. A note is a PRIVATE
draft only you see, ready to send to the team's Inbox for review.

Options:
  --content "<text>"   The note content (required). @path reads from a file.
  --project <id>       Optional — attach the note to a project (shows in project context[]).
  --card <id>          Optional — attach the note to a card mid-loop (needs --project).
  --suggest-topic      Hint that this note should become a topic proposal.
  --allow-secrets      Write even if the content looks like a secret.
  --json               Machine-readable output: { slug, title, url }.

Examples:
  driftless note add --content "OAuth expira cada 2h en IHCE — refrescar antes de enviar"
  driftless note add --content @notes/today.md --json
  driftless note add --content "Webhook llega fuera de orden — idempotencia" --project <pid> --card <cid>
`

export const AREA_HELP = `driftless area <subcommand> [args] [flags]

Areas — the navigation domains topics file under (Billing, Auth, Infra). File
a topic into one with 'context add/update --area <name|id>'.

Subcommands:
  area list                              List areas (with topic counts)
  area add <name> [--description "..."]  Create an area
  area rename <id> <new-name>            Rename an area (owner/admin)
  area rm <id>                           Delete an area (owner/admin)

Flags: --json (machine output)
`

export const AGENT_HELP = `driftless agent <subcommand> [args] [flags]

Agents — the headless workers (Auditor, Architect, Librarian) that review PRs,
propose topics, and tend the knowledge graph. They run automatically; this
command inspects their runs and tunes their config from the CLI.

Subcommands:
  agent runs [--limit n]                 Recent agent runs
  agent run <id>                         One run with its full trace
  agent config [--show]                  Show agent config (keys never echoed)
  agent config --set key=value ...       Set config (model, opencode_key,
                                          auditor/architect/librarian toggles,
                                          librarian_autovouch)
  agent usage [--days n]                 Token/cost usage over the last N days
  agent stats                            Proposal acceptance stats
  agent models                           Available models

Flags: --json (machine output)

Config keys (--set):
  model=<id>                  Model the agents run on
  opencode_key=<key>          BYOM key (write-only — never echoed back)
  auditor=<true|false>        Toggle the Auditor
  architect=<true|false>      Toggle the Architect
  librarian=<true|false>      Toggle the Librarian
  librarian_autovouch=<true|false>   Let the Librarian self-vouch its edits

Examples:
  driftless agent runs --limit 10
  driftless agent run run_abc123
  driftless agent config --show
  driftless agent config --set model=claude-opus-4 --set auditor=true
  driftless agent usage --days 30
`

export const AUDIT_HELP = `driftless audit <subcommand> [flags]

Audit — the workspace audit log (owner/admin).

Subcommands:
  audit log [--limit n]      Recent audit-log entries

Flags: --json (machine output)

Examples:
  driftless audit log
  driftless audit log --limit 50 --json
`

export const REPO_HELP = `driftless repo <subcommand> [args] [flags]

Repos — the GitHub repositories this workspace tracks. Linking a repo lets the
GitHub App attribute its pushes and PRs to your workspace (drift + the Auditor).

Subcommands:
  repo list                  List linked repos
  repo link <org/repo>       Link a GitHub repo (e.g. acme/billing)
  repo unlink <id>           Unlink a repo by id

Flags: --json (machine output)

Examples:
  driftless repo list
  driftless repo link acme/billing
  driftless repo unlink repo_abc123
`

// The canonical role vocabulary, used both in MEMBER_HELP and by member.ts's
// validation. Kept here so the help string stays a self-contained literal.
export const MEMBER_ROLES = ['owner', 'admin', 'member']

export const MEMBER_HELP = `driftless member <subcommand> [args] [flags]

Members — workspace membership and roles (owner/admin operations).

Subcommands:
  member list                            List workspace members
  member invite <email> [--role admin|member]   Invite someone (default: member)
  member role <memberId> <owner|admin|member>    Change a member's role
  member rm <memberId>                   Remove a member
  member invites                         List pending invitations

Roles: ${MEMBER_ROLES.join(' · ')}
Flags: --json (machine output)

Examples:
  driftless member list
  driftless member invite teammate@acme.com --role admin
  driftless member role mem_abc123 admin
  driftless member rm mem_abc123
  driftless member invites
`

export const EXPORT_HELP = `driftless export [--out <path>] [--json]

Export the full workspace bundle (topics, areas, relations, …) as JSON
(owner/admin). Writes to a file by default; --json prints to stdout.

Flags:
  --out <path>   Output file (default: driftless-export-<slug>.json)
  --json         Print the bundle to stdout instead of writing a file

Examples:
  driftless export
  driftless export --out backup.json
  driftless export --json > bundle.json
`

export const TAGS_HELP = `Usage: driftless tags [add|rename|rm] [args] [flags]

The workspace tag registry — pre-create and curate the tag vocabulary topics
anchor to. Attaching an unregistered tag via \`context add/update --tags\` never
fails (it auto-registers); the registry is curation, not a gate.

  driftless tags                          list tags with topic counts (paginated)
  driftless tags add <name> [--description "..."]    pre-create (idempotent)
  driftless tags rename <old> <new>       rename everywhere (rewrites topics; owner/admin)
  driftless tags rm <name>                delete + detach from every topic (owner/admin)

Flags:
  --limit N / --offset N    page through a large registry (default 50, most-used first)
  --description "..."       description shown in the dashboard and autocomplete
  --json                    machine-readable output

Filter topics by tag:
  driftless context search "<query>" --tag <name>     scope a search to a category
  driftless context list --tag <name>                 list a category (repeatable; OR)
`

export const INTEGRATION_HELP = `driftless integration <subcommand> [args] [flags]

Integrations — SETUP: connect, confirm and disconnect external providers. This
is the privileged connect flow; once a provider is connected you OPERATE it with
'driftless broker' (connections / operations / invoke / records / events).
SETUP (here) and EXECUTION (broker) are deliberately separate.

Subcommands:
  integration list                       List this workspace's integrations
  integration catalog                    Connectable providers (from Nango)
  integration connect <provider>         Start a connect session (returns the Connect UI session)
  integration confirm <provider>         Persist the authorized connection (run after connect)
  integration rm <id>                    Disconnect / delete an integration by id

Connect flow:
  1. integration catalog                 see what you can connect
  2. integration connect <provider>      authorize in the returned Connect UI
  3. integration confirm <provider>      persist it + enable the action catalog
  Then: driftless broker connections / operations <provider> / invoke <provider> <op>

Flags: --json (machine output)

Examples:
  driftless integration catalog
  driftless integration connect notion
  driftless integration confirm notion
  driftless integration list
  driftless integration rm int_abc123
`

export const BROKER_HELP = `driftless broker <subcommand> [flags]

Broker — OPERATE the providers you've already connected, through governed named
Operations. Credentials are resolved server-side and never reach the CLI; every
call is audited. Reads AND writes run inline (Driftless is a tooling proxy; the
human-in-the-loop is YOUR agent's harness, not a queue).

SETUP vs EXECUTION (these are different things):
  • Connecting/configuring a provider = integration SETUP — do it in the dashboard
    (Settings → Connections) or via the integrations connect flow. Run
    'driftless broker setup' for the exact steps. The broker does NOT connect or
    disconnect providers.
  • Everything below = broker EXECUTION — using a connection that already exists.

Operations are the Nango actions enabled for a connection's integration, served
from materialized metadata; '--refresh' re-pulls the live list from Nango.

Subcommands:
  broker setup                                How to connect/configure a provider
  broker connections                          List your connected providers
  broker capabilities <provider>              Show approved capabilities, setup health,
                                              disabled writes, and bounded read hints
  broker operations <provider> [--refresh]    List a connection's operations
  broker operations --query "<intent>"        Find an operation by intent ACROSS
                  [--provider <p>] [--effect read|write] [--risk low|medium|high] [--limit <n>] [--cursor <c>]   connections
  broker inspect <provider> <operation>       Full spec of ONE operation (schema,
                                              effect/risk/idempotency, criterion, policy)
  broker invoke <provider> <operation>        Run an operation (inline + audited)
                  [--input <json>|@file]
  broker models <provider>                    Discover importable record models (enabled syncs)
  broker records <provider> --model <m>       Read a synced model's records (mirrored,
                  [--modified-after <iso>] [--cursor <c>] [--limit <n>]   delta-aware)
  broker page <pageId> [--max-bytes <n>]      Read one Notion page's bounded, citeable
                                              content (read-only)
  broker index <provider> --dry-run           Preview explicit import/index into
                  [--model <m>] [--limit <n>] [--sample-limit <n>] [--max-bytes <n>]   Driftless-owned connector documents
  broker index <provider>                     Execute import/index after reviewing
                  [--model <m>] [--limit <n>] [--max-bytes <n>] [--import-run-id <id>]   the dry-run
  broker events [provider] [--limit <n>]      The inbound event feed (drift signal)
  broker criterion <provider>                 Show/attach criterion topics
                  [--add <slug>] [--rm <slug>] (the team's "how we navigate this")

Flags: --input <json>|@file · --query · --provider · --effect · --risk · --model · --modified-after · --cursor · --limit · --refresh · --dry-run · --sample-limit · --max-bytes · --import-run-id · --json

Examples:
  driftless broker setup
  driftless broker capabilities notion
  driftless broker operations notion --refresh
  driftless broker operations --query "create github issue" --effect write
  driftless broker inspect github create-issue
  driftless broker invoke notion search-pages --input '{}'
  driftless broker models notion
  driftless broker records notion --model User --modified-after 2026-06-01T00:00:00Z
  driftless broker page 2a1b3c4d-5e6f-7081-9a2b-3c4d5e6f7081
  driftless broker index notion --dry-run --model ContentMetadata
  driftless broker index notion --model ContentMetadata --import-run-id manual-1
  driftless broker events notion --limit 10
`
